import { AppManagerBillingPage } from '@';

// --- Constants ---
export const SDK_LOG_PREFIX = '[AppManager SDK]';
export const EVERY_30_DAYS = 'EVERY_30_DAYS';
export const ANNUAL = 'ANNUAL';
export const FEATURES_API_ENDPOINT = '/api/app-manager/plan-features';
export const PLANS_API_ENDPOINT = '/api/app-manager/plans';
export const PROCESS_PLAN_API_ENDPOINT = '/api/app-manager/plan/process';
export const PLAN_CHANGE_MODAL_DEFAULT_TITLE = 'Confirm plan change';
export const PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION = 'Are you sure you want to switch to this plan?';

export const INTERVAL_MAP = {
    [EVERY_30_DAYS]: { label: 'Monthly', short: 'mo' },
    [ANNUAL]: { label: 'Yearly', short: 'yr' }
};

// --- General Utility Functions ---
/** Deep equality check for objects/arrays */
export function deepEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

/** Safe JSON parse with fallback */
export function safeParse(value, fallback = {}) {
    try { return JSON.parse(value); } catch { return fallback; }
}

/** Emit custom event from an element */
export function emitEvent(element, name, detail = {}) {
    element.dispatchEvent(new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
    }));
}

// --- Translation Helpers ---
/** Translate a string using AppManagerBillingPage translations */
export function translateMe(key) {
    const value = AppManagerBillingPage.translateMe(key);
    if (typeof window !== 'undefined') {
        (window.__APP_MANAGER_TRANSLATIONS__ ??= new Set()).add(key);
    }
    return value;
}

// --- Plan Type Helpers ---
export function isFreePlan(plan) {
    return !!plan.public && !plan.is_external_charge && typeof plan.name === 'string' && plan.name.trim().toLowerCase() === 'free';
}

// --- Interval Helpers ---
export function getIntervalLabel(interval) {
    return translateMe(INTERVAL_MAP[interval]?.label || 'Monthly');
}
export function getIntervalShortLabel(interval) {
    return translateMe(INTERVAL_MAP[interval]?.short || 'mo');
}

// --- Plan/Feature Logic ---
/**
 * Calculate plan price details including discounts
 * @param {Object} plan - The plan object containing price and discount information
 * @param {Object|null} promotionalDiscount - Optional promotional discount to apply
 * @returns {Object} - Plan object with calculated price details
 */
export function calculatePlanPriceWithDiscounts(plan, promotionalDiscount = null) {
    try {
        const originalPrice = parseFloat(plan.price?.toString?.() ?? plan.price);
        if (isNaN(originalPrice)) {
            console.error(SDK_LOG_PREFIX, 'Invalid plan price:', plan.price);
            return { ...plan, price: 0, strike_price: null, has_discount: false };
        }
        let finalPrice = originalPrice;
        let hasDiscount = false;
        if (
            promotionalDiscount
            && promotionalDiscount?.value > 0
            && Array.isArray(promotionalDiscount.plan_relation)
            && promotionalDiscount.plan_relation.includes(plan.id)
        ) {
            hasDiscount = true;
            finalPrice = promotionalDiscount.type === 'percentage'
                ? originalPrice - (originalPrice * promotionalDiscount.value) / 100
                : Math.max(0, originalPrice - promotionalDiscount.value);
        } else if (plan.discount > 0) {
            hasDiscount = true;
            finalPrice = plan.discount_type === 'percentage'
                ? originalPrice - (originalPrice * plan.discount) / 100
                : Math.max(0, originalPrice - plan.discount);
        }
        return {
            ...plan,
            price: parseFloat(finalPrice.toFixed(2)),
            strike_price: hasDiscount ? parseFloat(originalPrice.toFixed(2)) : null,
            has_discount: hasDiscount
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in calculatePlanPriceWithDiscounts:', e, plan, promotionalDiscount);
        return { ...plan, price: 0, strike_price: null, has_discount: false };
    }
}

/**
 * Check if the given plan is the current active plan.
 * This returns true only when there is an active charge, a currentPlan exists,
 * and the plan either matches the currentPlan id or (for non-custom plans)
 * its base_plan matches the currentPlan id. Ensures "current" is tied to an active charge.
 */
export function isCurrentPlan(plan, currentPlan, hasActiveCharge = false) {
    return hasActiveCharge
        && currentPlan
        && (
            currentPlan.id === plan.id
            || (
                !plan.is_custom
                && plan.base_plan === currentPlan.id
            )
        );
}

/** Check if the plan is the same in another interval */
export function isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan) {
    return currentPlan && (plan.shopify_plans === shopifyPlan);
}

/**
 * Determine whether to show a store-base plan note below the plan.
 * Only applicable for store-base plans (`plan.store_base_plan`).
 * The note is shown when:
 * - a `currentPlan` exists,
 * - the `shopifyPlan` is listed in `plan.shopify_plans`,
 * - the plan is not the current active plan,
 * - and the plan is not the same plan in another interval.
 *
 * Use this to display the following note below the plan:
 * Note: On account of your recent Shopify plan upgrade, you should consider upgrading your current app plan
 */
export function isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge) {
    return !!(
        plan.store_base_plan
        && currentPlan
        && plan.shopify_plans.includes(shopifyPlan)
        && !isCurrentPlan(plan, currentPlan, hasActiveCharge)
        && !isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan)
    );
}

/**
 * Format a feature value for display
 * @param {Object} feature - Feature object
 * @param {Object} featureValues - Optional feature values
 * @returns {string}
 */
export function formatFeature(feature, featureValues) {
    try {
        if (["double", "integer"].includes(feature?.value_type)) {
            if (feature.format === "percentage") {
                return `${feature.value}%`;
            } else if (feature.format === "count") {
                return feature.value < 0 ? translateMe("Unlimited") : feature.value;
            } else {
                return translateMe(feature.value);
            }
        }
        if (feature?.value_type === "array") {
            let values = [];
            try {
                if (Array.isArray(feature.value)) {
                    values = feature.value;
                } else if (typeof feature.value === "string") {
                    values = JSON.parse(feature.value);
                }
            } catch (e) {
                console.error(SDK_LOG_PREFIX, "Error parsing array feature value:", e, feature.value);
                return "";
            }
            return Array.isArray(values) ? values.join(",") : "";
        }
        if (feature?.value_type === "boolean") {
            return feature.value ? translateMe("Yes") : translateMe("No");
        }
        if (feature?.value_type === "string") {
            const cleanValue = typeof feature.value === "string"
                ? feature.value.replace(/"/g, "")
                : "";
            return translateMe(cleanValue);
        }
        return "";
    } catch (e) {
        console.error(SDK_LOG_PREFIX, "Error in formatFeature:", e, feature, featureValues);
        return "";
    }
}

/**
 * Determine if a plan button should be disabled
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {boolean} hasActiveCharge
 * @param {Object} currentPlan
 * @returns {boolean}
 */
export function isPlanButtonDisabled(shopifyPlan, plan, hasActiveCharge, currentPlan) {
    if (isCurrentPlan(plan, currentPlan, hasActiveCharge)) {
        return true;
    }

    return !!(plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan));
}

/**
 * Get the button text for a plan
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {Object} currentPlan
 * @param {boolean} isPlanTable
 * @param {boolean} hasActiveCharge
 * @returns {string}
 */
export function getPlanButtonText(shopifyPlan, plan, currentPlan, isPlanTable = false, hasActiveCharge = false) {
    const isCurrentPlanFlag = isCurrentPlan(plan, currentPlan, hasActiveCharge);
    if (isCurrentPlanFlag) {
        return translateMe("Selected plan");
    }

    if (plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan)) {
        return translateMe("Not applicable");
    }

    return translateMe("Choose plan");
}

/**
 * Filter, map, and sort plans based on interval, discount, and note logic.
 * @param {Array} plans - Array of plan objects
 * @param {string} interval - Interval constant (e.g. EVERY_30_DAYS, ANNUAL)
 * @param {object} promotionalDiscount - Discount object
 * @param {string} shopifyPlan - Shopify plan name
 * @param {object} currentPlan - Current plan object
 * @param {boolean} hasActiveCharge - Whether there is an active charge
 * @returns {{anyPlanHasNote: boolean, filteredPlans: {price: number, has_discount: boolean, strike_price: (number|null)}[], anyPlanHasDiscount: boolean}} Filtered, mapped, and sorted plans
 */
export function getFilteredSortedPlans(plans, interval, promotionalDiscount, shopifyPlan, currentPlan, hasActiveCharge) {
    let anyPlanHasDiscount = false;
    let anyPlanHasNote = false;
    let filteredPlans;
    try {
        filteredPlans = (Array.isArray(plans) ? plans : []).filter(plan => isFreePlan(plan) || plan.interval === interval)
            .map(plan => {
                const planDetails = calculatePlanPriceWithDiscounts(plan, promotionalDiscount);
                if (planDetails.has_discount && !anyPlanHasDiscount) {
                    anyPlanHasDiscount = true;
                }
                if (
                    plan.store_base_plan &&
                    !anyPlanHasNote &&
                    isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge)
                ) {
                    anyPlanHasNote = true;
                }
                return planDetails;
            })
            .sort((a, b) => a.price - b.price);
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getFilteredSortedPlans:', e, plans);
        filteredPlans = [];
    }
    return { filteredPlans, anyPlanHasDiscount, anyPlanHasNote };
}

export function formatString(value) {
    if (typeof value === 'string') {
        return value.toLowerCase().replace(/\s+/g, '-');
    }
    return value;
}

// --- Plan Details / Highlights Helpers ---

/**
 * Normalize a sort order value.
 * Uses Number() instead of parseInt() || fallback to correctly handle sort_order: 0.
 * @param {*} value
 * @param {number} [fallback=999]
 * @returns {number}
 */
export function normalizeSortOrder(value, fallback = 999) {
    const n = Number(value);
    return Number.isNaN(n) ? fallback : n;
}

/**
 * Get highlight-type entries from plan.details, sorted by sort_order.
 * Normalized into a feature-compatible shape (value_type: 'boolean').
 * @param {Object} plan
 * @returns {Array}
 */
export function getSortedPlanHighlights(plan) {
    try {
        return Object.values(plan.details || {})
            .filter(highlight => highlight.type === 'highlight')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(highlight => ({
                uuid: `highlight_${highlight.id}`,
                value: '1',
                value_type: 'boolean',
                name: highlight.text,
            }));
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getSortedPlanHighlights:', e, plan);
        return [];
    }
}

/**
 * Get detail-type entries from plan.details, sorted by sort_order.
 * Normalized into a feature-compatible shape (value_type: 'boolean').
 * @param {Object} plan
 * @returns {Array}
 */
export function getSortedPlanDetails(plan) {
    try {
        return Object.values(plan.details || {})
            .filter(detail => detail.type === 'detail')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(detail => ({
                uuid: `detail_${detail.id}`,
                value: '1',
                value_type: 'boolean',
                name: detail.text,
            }));
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getSortedPlanDetails:', e, plan);
        return [];
    }
}

/** First billing detail — always shown below the plan card */
export function getPrimaryPlanDetail(plan) {
    const details = getSortedPlanDetails(plan);
    return details.length > 0 ? details[0] : null;
}

/** Remaining billing details after the first — shown in a tooltip */
export function getAdditionalPlanDetails(plan) {
    const details = getSortedPlanDetails(plan);
    return details.length > 1 ? details.slice(1) : [];
}

/**
 * Merge plan highlights + sorted plan features into a single capped list.
 * Highlights always come first; remaining slots are filled by features sorted by popularity/display_order.
 *
 * @param {Object}  plan              - Plan object (requires plan.details and plan.features)
 * @param {Array}   allFeatures       - Full feature definition list from the component
 * @param {number}  [max=4]           - Maximum items to return
 * @param {boolean} [showOnlyHighlights=false] - When true, show only highlights (if any exist); fall back to merged list otherwise
 * @returns {Array}
 */
export function preparePlanFeatures(plan, allFeatures, max = 4, showOnlyHighlights = false) {
    try {
        const highlights = getSortedPlanHighlights(plan);

        const assignedUuids = new Set(Object.keys(plan.features || {}));
        const sortedFeatures = (Array.isArray(allFeatures) ? allFeatures : [])
            .filter(feature => assignedUuids.has(feature.uuid) && !feature.hidden_feature)
            .sort((a, b) => {
                const popularityA = normalizeSortOrder(a.popularity);
                const popularityB = normalizeSortOrder(b.popularity);
                if (popularityA !== popularityB) return popularityA - popularityB;
                return normalizeSortOrder(a.display_order) - normalizeSortOrder(b.display_order);
            })
            .map(feature => ({
                ...feature,
                value: plan.features[feature.uuid]?.value ?? plan.features[feature.uuid],
            }));

        // When showOnlyHighlights is true and highlights exist, return only highlights
        if (showOnlyHighlights && highlights.length > 0) {
            return highlights;
        }

        // Default: highlights first, fill remaining slots with features
        const remaining = Math.max(0, max - highlights.length);
        return [...highlights, ...sortedFeatures.slice(0, remaining)];
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in preparePlanFeatures:', e, plan);
        return [];
    }
}

export function isPlanDowngrade(plan, currentPlan, hasActiveCharge) {
    if (!hasActiveCharge || !currentPlan?.id) return false;
    const newPrice = parseFloat(plan?.price);
    const currentPrice = parseFloat(currentPlan?.price);
    return !isNaN(newPrice) && !isNaN(currentPrice) && newPrice < currentPrice;
}

/**
 * Resolve plan-change confirmation copy from plan.details.
 * Uses all records of the type ` plan-change-confirmation `, sorted by sort_order.
 * - 2+ records: first is the title, the remaining are description lines
 * - 1 record: treated as description only (title falls back to default in UI)
 */
export function getPlanChangeConfirmationContent(plan) {
    try {
        const entries = Object.values(plan?.details || {})
            .filter(detail => detail?.type === 'plan-change-confirmation')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(detail => (typeof detail?.text === 'string' ? detail.text.trim() : ''))
            .filter(Boolean);

        if (entries.length >= 2) {
            return {
                title: entries[0],
                descriptionLines: entries.slice(1),
                // keep single-description compatibility for older consumers
                description: entries[1],
            };
        }
        if (entries.length === 1) {
            return {
                title: null,
                descriptionLines: [entries[0]],
                description: entries[0],
            };
        }
        return { title: null, descriptionLines: [], description: null };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getPlanChangeConfirmationContent:', e, plan);
        return { title: null, descriptionLines: [], description: null };
    }
}

/**
 * Normalize modal copy for plan-change confirmation UI.
 * Accepts a precomputed confirmation payload and returns a stable shape:
 * { title, description, descriptionLines }.
 */
export function preparePlanChangeModalContent(content = {}, fallback = {}) {
    try {
        const rawLines = Array.isArray(content?.descriptionLines)
            ? content.descriptionLines
            : (typeof content?.description === 'string' && content.description.trim() ? [content.description] : []);

        const descriptionLines = rawLines
            .map(line => (typeof line === 'string' ? line.trim() : ''))
            .filter(Boolean);

        const fallbackTitle = fallback?.title ?? PLAN_CHANGE_MODAL_DEFAULT_TITLE;
        const fallbackDescription = fallback?.description ?? PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION;

        return {
            title: typeof content?.title === 'string' && content.title.trim() ? content.title.trim() : fallbackTitle,
            descriptionLines,
            description: descriptionLines[0] || (typeof content?.description === 'string' && content.description.trim() ? content.description.trim() : fallbackDescription),
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in preparePlanChangeModalContent:', e, content, fallback);
        return {
            title: fallback?.title ?? PLAN_CHANGE_MODAL_DEFAULT_TITLE,
            descriptionLines: [],
            description: fallback?.description ?? PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION,
        };
    }
}

export function createEmptyPlanChangeModalContent() {
    return { title: null, description: null, descriptionLines: [] };
}

/**
 * Normalize plan-change confirmation request payload emitted by child components.
 * Returns null when source/plan is invalid for the provided component list.
 */
export function getPlanChangeRequestFromEvent(event, validSources = []) {
    try {
        const source = event?.detail?.source;
        const plan = event?.detail?.plan;
        if (!Array.isArray(validSources) || !validSources.includes(source) || !plan || !plan.id) {
            return null;
        }
        return {
            source,
            plan,
            content: {
                title: event?.detail?.title || null,
                description: event?.detail?.description || null,
                descriptionLines: Array.isArray(event?.detail?.descriptionLines) ? event.detail.descriptionLines : [],
            },
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getPlanChangeRequestFromEvent:', e, event, validSources);
        return null;
    }
}

export function escapeHTML(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function formatTextWithLineBreaks(text) {
    if (typeof text !== 'string') return '';
    return escapeHTML(text).replace(/\n/g, '<br>');
}